import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Row, Col, Nav, Tab, Accordion,
  Button,
} from 'react-bootstrap5';
import axios from 'axios';

import routes from 'app/routes';
import LoadingPlaceholder from 'components/new_ui/WaitLayers/LoadingPlaceholder';
import ExternalLink from 'components/Widgets/ExternalLink';
import IconWithText from 'components/Widgets/IconWithText';

import { useParams, useNavigate } from 'react-router-dom';
import { useContext as useUsersContext } from 'components/Users/Context';
import Summary from './Summary';
import Details from './Details';

export default function Index({
  onNextStep, title, subTitle, description,
}) {
  const { state: stateUser } = useUsersContext();
  const { programId } = useParams();

  const [talentAssessment, setTalentAssessment] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(routes.api.career_pulse_assessment_reports.latest)
      .then((response) => {
        setTalentAssessment(response.data.data);
      });
  }, []);

  if (talentAssessment === null) {
    return (
      <div className="content">
        <section>
          <LoadingPlaceholder text="Loading..." />
        </section>
      </div>
    );
  }

  return (
    <div className="content">
      <section>
        <Row className="align-items-center">
          <Col>
            <h1>{title}</h1>
          </Col>
          {stateUser.user && !stateUser.user.attributes.is_mobile_login && (
          <Col sm="auto" className="mt-3 mt-sm-0">
            <ExternalLink
              href={talentAssessment.attributes.url}
            >
              Download
            </ExternalLink>
          </Col>
          )}
        </Row>
      </section>

      <section>
        <h4 className="mb-3">{subTitle}</h4>

        {description}
      </section>

      <Tab.Container
        id="talent-assessment-report"
        defaultActiveKey="talent-assessment-summary"
      >
        <Nav fill justify variant="tabs">
          <Nav.Item>
            <Nav.Link eventKey="talent-assessment-summary" className="icon-with-text">
              <i className="summary-icon" />
              <div className="text-content">
                Summary
              </div>
            </Nav.Link>
          </Nav.Item>

          <Nav.Item>
            <Nav.Link eventKey="talent-assessment-productivity" className="icon-with-text">
              <i className="productivity-icon" />
              <div className="text-content">
                Productivity
              </div>
            </Nav.Link>
          </Nav.Item>

          <Nav.Item>
            <Nav.Link eventKey="talent-assessment-wellbeing" className="icon-with-text">
              <i className="wellbeing-icon" />
              <div className="text-content">
                Wellbeing
              </div>
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Accordion defaultActiveKey="talent-assessment-summary">
          <Tab.Content>
            <Tab.Pane eventKey="talent-assessment-summary" className="px-sm-4 px-xxl-5">
              <Accordion.Item eventKey="talent-assessment-summary">
                <Accordion.Header>
                  <IconWithText
                    iconClassName="summary-icon"
                    wrapperClassName="ms-2"
                  >
                    Summary
                  </IconWithText>
                </Accordion.Header>

                <Accordion.Body>
                  <Summary talentAssessment={talentAssessment} />
                </Accordion.Body>
              </Accordion.Item>
            </Tab.Pane>

            <Tab.Pane eventKey="talent-assessment-productivity" className="px-sm-4 px-xxl-5">
              <Accordion.Item eventKey="talent-assessment-productivity">
                <Accordion.Header>
                  <IconWithText
                    iconClassName="productivity-icon"
                    wrapperClassName="ms-2"
                  >
                    Productivity
                  </IconWithText>
                </Accordion.Header>

                <Accordion.Body>
                  <Details collection={talentAssessment.attributes.productivity_summary} />
                </Accordion.Body>
              </Accordion.Item>
            </Tab.Pane>

            <Tab.Pane eventKey="talent-assessment-wellbeing" className="px-sm-4 px-xxl-5">
              <Accordion.Item eventKey="talent-assessment-wellbeing">
                <Accordion.Header>
                  <IconWithText
                    iconClassName="wellbeing-icon"
                    wrapperClassName="ms-2"
                  >
                    Wellbeing
                  </IconWithText>
                </Accordion.Header>

                <Accordion.Body>
                  <Details collection={talentAssessment.attributes.wellbeing_summary} />
                </Accordion.Body>
              </Accordion.Item>
            </Tab.Pane>
          </Tab.Content>
        </Accordion>
      </Tab.Container>

      {onNextStep !== undefined && (
        <footer className="d-flex justify-content-end mt-3">
          <Button onClick={onNextStep}>
            Next
          </Button>
        </footer>
      )}

      {programId !== undefined && onNextStep === undefined && (
        <footer className="d-flex justify-content-end mt-3">
          <Button onClick={() => navigate(
            stateUser.user && stateUser.user.attributes.is_mobile_login ? routes.members.webview.finish
              : routes.members.programs.show(programId),
          )}
          >
            Next
          </Button>
        </footer>
      )}

    </div>
  );
}

Index.propTypes = {
  onNextStep: PropTypes.func,
  title: PropTypes.string,
  subTitle: PropTypes.string,
  description: PropTypes.node,
};

Index.defaultProps = {
  onNextStep: undefined,
  title: 'Talent Assessment',
  subTitle: 'Your Talent Assessment Report is Ready!',
  description: (
    <p>
      Based on your Talent Assessment, we&apos;ve created a comprehensive profile
      highlighting your strengths and development opportunities. Your report
      has been emailed to you automatically, or you can download it here.
    </p>),
};
